import type { ProctorCredentials } from './types';

// localStorage persistence for the BeExpert proctoring session, so a page
// refresh OR tab close mid-test can restore and re-attach to the same
// session. NOTE: localStorage is shared across tabs and persists until
// explicitly cleared - there is one slot, keyed to the last-provisioned quiz.
const STORAGE_KEYS = {
  SESSION_ID: 'bx_sessionId',
  TOKEN: 'bx_token',
  REFRESH_TOKEN: 'bx_refreshToken',
  TOKEN_TTL: 'bx_accessTokenTTL',
  TENANT_ID: 'bx_tenantId',
  API_URL: 'bx_apiBaseUrl',
  QUIZ_UID: 'bx_quizUID',
  QUIZ_TITLE: 'bx_title',
  QUIZ_DURATION: 'bx_scheduledDurationSeconds',
  QUIZ_START_TIME: 'bx_scheduledStartAt'
};

interface QuizMeta {
  quizUID: string;
  quizTitle?: string;
  quizDuration?: number;
}

export const storeCredentials = (
  sessionData: ProctorCredentials,
  { quizUID, quizTitle, quizDuration }: QuizMeta
): void => {
  if (typeof window === 'undefined') return;

  try {
    // Use CURRENT time as the session start (not the quiz's scheduled_at,
    // which can be in the past) - token expiry is computed from this.
    const actualSessionStartTime = new Date().toISOString();

    localStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionData.sessionId);
    localStorage.setItem(STORAGE_KEYS.TOKEN, sessionData.sessionToken);
    localStorage.setItem(
      STORAGE_KEYS.TOKEN_TTL,
      String(sessionData.accessTokenTTL)
    );
    localStorage.setItem(STORAGE_KEYS.API_URL, sessionData.apiUrl);
    localStorage.setItem(STORAGE_KEYS.QUIZ_UID, quizUID);
    localStorage.setItem(STORAGE_KEYS.QUIZ_TITLE, quizTitle);
    localStorage.setItem(STORAGE_KEYS.QUIZ_DURATION, String(quizDuration));
    localStorage.setItem(STORAGE_KEYS.QUIZ_START_TIME, actualSessionStartTime);
  } catch (error: any) {
    // no-ops
  }
};

export const retrieveStoredCredentials = (): ProctorCredentials | null => {
  if (typeof window === 'undefined') return null;

  try {
    const sessionId = localStorage.getItem(STORAGE_KEYS.SESSION_ID);
    const sessionToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
    const apiUrl = localStorage.getItem(STORAGE_KEYS.API_URL);
    if (!sessionId || !sessionToken || !apiUrl) return null;

    return {
      sessionId,
      sessionToken,
      apiUrl,
      accessTokenTTL: parseInt(localStorage.getItem(STORAGE_KEYS.TOKEN_TTL), 10)
    };
  } catch (error: any) {
    return null;
  }
};

export const getStoredQuizUID = (): string | null => {
  if (typeof window === 'undefined') return null;

  try {
    return localStorage.getItem(STORAGE_KEYS.QUIZ_UID);
  } catch (error: any) {
    return null;
  }
};

export const clearStoredCredentials = (): void => {
  if (typeof window === 'undefined') return;

  try {
    Object.values(STORAGE_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });
  } catch (error: any) {
    // no-ops
  }
};
